import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { sanitizeRedirect } from "../../lib/redirect";
import AuthShell from "./AuthShell";

export default function VerifyEmailPage() {
  const { verifyEmail, resendVerification } = useAuth();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token");
  const pendingEmail = params.get("email");
  const isPendingView = params.get("pending") === "1";
  // Preserve the original destination the user was heading to before hitting
  // the auth flow (PDP, try-on, wishlist, etc.). Falls back to "/" for any
  // manipulated / external redirect.
  const redirect = sanitizeRedirect(params.get("redirect"));

  const [state, setState] = useState(isPendingView ? "pending" : (token ? "verifying" : "error"));
  const [message, setMessage] = useState("");
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (!token || isPendingView) return;
    let cancel = false;
    (async () => {
      try {
        await verifyEmail(token);
        if (!cancel) {
          setState("success");
          setTimeout(() => nav(redirect, { replace: true }), 2500);
        }
      } catch (e) {
        if (!cancel) {
          setState("error");
          setMessage(e?.response?.data?.detail || "Não foi possível confirmar o e-mail.");
        }
      }
    })();
    return () => { cancel = true; };
  }, [token]);

  const onResend = async () => {
    if (!pendingEmail) return;
    setResending(true);
    try {
      await resendVerification(pendingEmail);
      setMessage("E-mail reenviado. Verifique sua caixa de entrada e a pasta de spam.");
    } catch {
      setMessage("Não foi possível reenviar agora. Tente novamente em instantes.");
    } finally {
      setResending(false);
    }
  };

  if (state === "pending") {
    return (
      <AuthShell
        title="Confirme seu e-mail"
        subtitle={pendingEmail ? `Enviamos um link de confirmação para ${pendingEmail}.` : "Enviamos um link de confirmação para o seu e-mail."}
        footer={<Link to="/login" className="auth-link" data-testid="link-to-login">Voltar para login</Link>}
      >
        <div className="text-center" data-testid="verify-pending">
          <p className="text-[15px] text-[#4A4A4A] leading-relaxed mb-6">
            Clique no link enviado ao seu e-mail para ativar sua conta. Enquanto não confirmar,
            o login ficará bloqueado.
          </p>
          {message && <p className="text-sm text-[#6B6B6B] mb-4" data-testid="verify-message">{message}</p>}
          <button onClick={onResend} disabled={resending || !pendingEmail} className="auth-cta auth-cta-secondary" data-testid="resend-btn">
            {resending ? "Reenviando…" : "Reenviar e-mail"}
          </button>
        </div>
      </AuthShell>
    );
  }

  if (state === "verifying") {
    return (
      <AuthShell title="Confirmando seu e-mail…" subtitle="Aguarde um instante.">
        <div className="text-center py-6">
          <div className="w-3 h-3 bg-[#B8A88A] rounded-full mx-auto animate-pulse" />
        </div>
      </AuthShell>
    );
  }

  if (state === "success") {
    return (
      <AuthShell title="E-mail confirmado" subtitle="Você já está conectada. Redirecionando…">
        <p className="text-center text-[#6B6B6B] text-sm" data-testid="verify-success">Bem-vinda à Prize Joias ✦</p>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Não foi possível confirmar"
      subtitle={message || "Link inválido ou expirado."}
      footer={<Link to="/login" className="auth-link" data-testid="link-to-login">Voltar para login</Link>}
    >
      <p className="text-center text-sm text-[#6B6B6B]" data-testid="verify-error">
        Tente solicitar um novo e-mail de confirmação ou contate o suporte.
      </p>
    </AuthShell>
  );
}
